import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import styles from './download-design-size-option.module.scss';
import {ControlledSlider} from '@Components/controlled-slider';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {VideoDesignSizeOptionType} from '@Libraries/poster-download-library';

enum PosterTypeUnits {
  PX = 'px',
  INCH = 'in',
  CM = 'cm',
  MM = 'mm',
  FT = 'ft',
}

const MM_PER_INCH = 25.4;
const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;

export function DownloadDesignSizeOption(): ReactElement | null {
  const {scale, setScale, posterObject, fileType, videoSliderScale, setVideoSliderScale, videoScale, setVideoScale} = useDownloadPanelData();
  const [min, setMin] = useState(0.1);
  const [max, setMax] = useState(4);
  const currentScale = scale ?? 1;
  const name = posterObject.type.name;
  const [doShowOriginalValueForVideo, setDoShowOriginalValueForVideo] = useState(false);
  const [videoOriginalDimensionSliderValue, setVideoOriginalDimensionSliderValue] = useState<number | null>(null);
  const [videoOriginalMatchingValue, setVideoOriginalMatchingValue] = useState<VideoDesignSizeOptionType | null>();
  const [mapOfVideoSliderValuesWithType, setMapOfVideoSliderValuesWithType] = useState<Record<number, VideoDesignSizeOptionType>>();

  const isCustomSize = (): boolean => {
    return name === 'poster-custom' || name === 'poster-custom-print' || name.includes('poster-custom-user');
  };

  const getMinMaxFromUnits = (units: PosterTypeUnits): {min: number; max: number} => {
    switch (units) {
      case PosterTypeUnits.MM:
        return {
          min: 20,
          max: 2400,
        };
      case PosterTypeUnits.CM:
        return {
          min: 2,
          max: 240,
        };
      case PosterTypeUnits.FT:
        return {
          min: 0.1,
          max: 8,
        };
      case PosterTypeUnits.INCH:
        return {
          min: 1,
          max: 96,
        };
      case PosterTypeUnits.PX:
      default:
        return {
          min: 50,
          max: 8000,
        };
    }
  };

  const getUnitsFromPosterTypeObject = (): PosterTypeUnits => {
    if (isCustomSize()) {
      return posterObject.units as PosterTypeUnits;
    }
    return (posterObject.type.units as PosterTypeUnits) ?? PosterTypeUnits.PX;
  };

  const getWidthHeightFromPosterObject = (): {width: number; height: number} => {
    switch (getUnitsFromPosterTypeObject()) {
      case PosterTypeUnits.MM:
        return {
          width: posterObject.type.widthMm,
          height: posterObject.type.heightMm,
        };
      case PosterTypeUnits.FT:
        return {
          width: posterObject.type.widthInch / 12,
          height: posterObject.type.heightInch / 12,
        };
      case PosterTypeUnits.INCH:
        return {
          width: posterObject.type.widthInch,
          height: posterObject.type.heightInch,
        };
      case PosterTypeUnits.CM:
        return {
          width: posterObject.type.widthCm,
          height: posterObject.type.heightCm,
        };
      case PosterTypeUnits.PX:
      default:
        return {
          width: posterObject.type.width,
          height: posterObject.type.height,
        };
    }
  };

  const getMinMaxForImagePosters = (): {min: number; max: number} => {
    const data = getMinMaxFromUnits(getUnitsFromPosterTypeObject());

    if (isCustomSize()) {
      return {
        min: data.min / Math.min(posterObject.userWidth, posterObject.userHeight),
        max: data.max / Math.max(posterObject.userWidth, posterObject.userHeight),
      };
    }

    const {width, height} = getWidthHeightFromPosterObject();

    return {
      min: data.min / Math.min(width, height),
      max: data.max / Math.max(width, height),
    };
  };

  const getMinMaxForGIF = (): {min: number; max: number} => {
    const {width, height} = getWidthAndHeight();
    const {width: pixelWidth, height: pixelHeight} = getPixelValueForDPI(width, height, getUnitsFromPosterTypeObject(), posterObject.type.dpi);
    const min = 50;
    const max = 2000;

    return {
      min: min / Math.min(pixelWidth, pixelHeight),
      max: max / Math.max(pixelWidth, pixelHeight),
    };
  };

  useEffect(() => {
    if (fileType === FileTypeOptions.GIF) {
      const {min, max} = getMinMaxForGIF();
      setMin(Math.max(min, 0.01));
      setMax(max);
      setScale(Math.min(max, 1));
      return;
    }

    const {min, max} = getMinMaxForImagePosters();
    setMin(Math.max(min, 0.1));
    setMax(max);
  }, [posterObject.type, fileType]);

  const setScaleForVideo = (value: number): void => {
    const scaledDimension = getScaledDimensionFromValue(value);
    const {width, height} = getWidthAndHeight();

    const {width: convertedWidth, height: convertedHeight} = getPixelValueForDPI(width, height, getUnitsForDisplay(), posterObject.type.dpi);

    if (convertedWidth < convertedHeight) {
      setScale(scaledDimension / convertedHeight);
    } else {
      setScale(scaledDimension / convertedWidth);
    }
  };

  useEffect(() => {
    const areUnitsInPixel = getUnitsFromPosterTypeObject() === PosterTypeUnits.PX;
    if (fileType === FileTypeOptions.MP4 && areUnitsInPixel) {
      const maxValue = Math.max(isCustomSize() ? posterObject.userWidth : posterObject.type.width, isCustomSize() ? posterObject.userHeight : posterObject.type.height);
      if (maxValue === 640 || maxValue === 1280 || maxValue === 1920 || maxValue === 3840) {
        let value = 2;
        switch (maxValue) {
          case 640:
            value = 0;
            setVideoOriginalMatchingValue(VideoDesignSizeOptionType.SD);
            break;
          case 1280:
            value = 1;
            setVideoOriginalMatchingValue(VideoDesignSizeOptionType.HD);
            break;
          case 1920:
            value = 2;
            setVideoOriginalMatchingValue(VideoDesignSizeOptionType.FULL_HD);
            break;
          case 3840:
            value = 3;
            setVideoOriginalMatchingValue(VideoDesignSizeOptionType.UHD_4K);
            break;
        }
        setVideoOriginalDimensionSliderValue(value);
      } else {
        if (maxValue < 640) {
          setVideoOriginalDimensionSliderValue(0);
          setVideoSliderScale(0);
          setMapOfVideoSliderValuesWithType({
            0: VideoDesignSizeOptionType.ORIGINAL,
            1: VideoDesignSizeOptionType.SD,
            2: VideoDesignSizeOptionType.HD,
            3: VideoDesignSizeOptionType.FULL_HD,
            4: VideoDesignSizeOptionType.UHD_4K,
          });
        } else if (maxValue < 1280) {
          setVideoOriginalDimensionSliderValue(1);
          setVideoSliderScale(1);
          setMapOfVideoSliderValuesWithType({
            0: VideoDesignSizeOptionType.SD,
            1: VideoDesignSizeOptionType.ORIGINAL,
            2: VideoDesignSizeOptionType.HD,
            3: VideoDesignSizeOptionType.FULL_HD,
            4: VideoDesignSizeOptionType.UHD_4K,
          });
        } else if (maxValue < 1920) {
          setVideoOriginalDimensionSliderValue(2);
          setVideoSliderScale(2);
          setMapOfVideoSliderValuesWithType({
            0: VideoDesignSizeOptionType.SD,
            1: VideoDesignSizeOptionType.HD,
            2: VideoDesignSizeOptionType.ORIGINAL,
            3: VideoDesignSizeOptionType.FULL_HD,
            4: VideoDesignSizeOptionType.UHD_4K,
          });
        } else if (maxValue < 3840) {
          setVideoOriginalDimensionSliderValue(3);
          setVideoSliderScale(3);
          setMapOfVideoSliderValuesWithType({
            0: VideoDesignSizeOptionType.SD,
            1: VideoDesignSizeOptionType.HD,
            2: VideoDesignSizeOptionType.FULL_HD,
            3: VideoDesignSizeOptionType.ORIGINAL,
            4: VideoDesignSizeOptionType.UHD_4K,
          });
        } else {
          setVideoSliderScale(3);
          setVideoScale(VideoDesignSizeOptionType.UHD_4K);
          setMapOfVideoSliderValuesWithType({
            0: VideoDesignSizeOptionType.SD,
            1: VideoDesignSizeOptionType.HD,
            2: VideoDesignSizeOptionType.FULL_HD,
            3: VideoDesignSizeOptionType.UHD_4K,
          });
          setDoShowOriginalValueForVideo(false);
          setVideoOriginalMatchingValue(null);
          setScale(3840 / maxValue);
          return;
        }
        setVideoScale(VideoDesignSizeOptionType.ORIGINAL);
        setDoShowOriginalValueForVideo(true);
      }
      setScale(1);
    }

    if (fileType === FileTypeOptions.MP4) {
      if (!areUnitsInPixel) {
        setDoShowOriginalValueForVideo(false);
        setVideoOriginalMatchingValue(null);
        setVideoScale(VideoDesignSizeOptionType.FULL_HD);
        setVideoSliderScale(2);
        setScaleForVideo(2);
      }
    } else if (fileType !== FileTypeOptions.GIF) {
      setScale(1);
    }
  }, [fileType, posterObject.type]);

  if (fileType === FileTypeOptions.PDF) {
    return null;
  }

  const roundToTwoDecimals = (num: number): number => {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  };

  const getUnitsForDisplay = (): PosterTypeUnits => {
    return getUnitsFromPosterTypeObject();
  };

  const onSlide = (value: number): void => {
    const dp = 1;
    const roundedValue = Number(value.toFixed(dp));
    setScale(roundedValue);
  };

  const onSlideGifScale = (value: number): void => {
    if (value > max || value < min) {
      return;
    }
    const dp = 2;
    const roundedValue = Number(value.toFixed(dp));
    setScale(roundedValue);
  };

  const getScaledDimensionFromValue = (value: number): number => {
    const areUnitsInPixel = getUnitsFromPosterTypeObject() === PosterTypeUnits.PX;
    if (doShowOriginalValueForVideo && mapOfVideoSliderValuesWithType && areUnitsInPixel) {
      const currentOption = mapOfVideoSliderValuesWithType[value];
      switch (currentOption) {
        case VideoDesignSizeOptionType.SD:
          return 640;
        case VideoDesignSizeOptionType.HD:
          return 1280;
        case VideoDesignSizeOptionType.FULL_HD:
          return 1920;
        case VideoDesignSizeOptionType.UHD_4K:
          return 3840;
        case VideoDesignSizeOptionType.ORIGINAL:
          throw new Error('Original size should be handled separately');
        default:
          return 1080;
      }
    }
    switch (value) {
      case 0:
        return 640;
      case 1:
        return 1280;
      case 2:
        return 1920;
      case 3:
        return 3840;
      default:
        return 1280;
    }
  };

  const getPixelValueForDPI = (width: number, height: number, units: PosterTypeUnits, dpi: number): {width: number; height: number} => {
    let widthInInches: number;
    let heightInInches: number;
    switch (units) {
      case PosterTypeUnits.INCH:
        widthInInches = roundToTwoDecimals(width);
        heightInInches = roundToTwoDecimals(height);
        break;

      case PosterTypeUnits.MM:
        widthInInches = roundToTwoDecimals(width / MM_PER_INCH);
        heightInInches = roundToTwoDecimals(height / MM_PER_INCH);
        break;

      case PosterTypeUnits.CM:
        widthInInches = roundToTwoDecimals(width / CM_PER_INCH);
        heightInInches = roundToTwoDecimals(height / CM_PER_INCH);
        break;

      case PosterTypeUnits.FT:
        widthInInches = roundToTwoDecimals(width * INCHES_PER_FOOT);
        heightInInches = roundToTwoDecimals(height * INCHES_PER_FOOT);
        break;
      case PosterTypeUnits.PX:
      default:
        return {width: width, height: height};
    }

    return {
      width: Math.round(widthInInches * dpi),
      height: Math.round(heightInInches * dpi),
    };
  };

  const onSlideVideoScale = (value: number): void => {
    setVideoSliderScale(value);

    if (doShowOriginalValueForVideo) {
      if (videoOriginalDimensionSliderValue !== null && value === videoOriginalDimensionSliderValue) {
        setVideoScale(VideoDesignSizeOptionType.ORIGINAL);
        setScale(1);
      } else {
        setScaleForVideo(value);
        setVideoScale(mapOfVideoSliderValuesWithType ? mapOfVideoSliderValuesWithType[value] : VideoDesignSizeOptionType.HD);
      }

      return;
    }

    setScaleForVideo(value);
    switch (value) {
      case 0:
        setVideoScale(VideoDesignSizeOptionType.SD);
        break;
      case 1:
        setVideoScale(VideoDesignSizeOptionType.HD);
        break;
      case 2:
        setVideoScale(VideoDesignSizeOptionType.FULL_HD);
        break;
      case 3:
        setVideoScale(VideoDesignSizeOptionType.UHD_4K);
        break;
    }
  };

  const getWidthAndHeight = (): {width: number; height: number} => {
    if (isCustomSize()) {
      return {
        width: Number(posterObject.userWidth),
        height: Number(posterObject.userHeight),
      };
    }

    return getWidthHeightFromPosterObject();
  };

  const getValueForDisplayForGif = (): string => {
    const {width, height} = getWidthAndHeight();
    const {width: convertedWidth, height: convertedHeight} = getPixelValueForDPI(width, height, getUnitsFromPosterTypeObject(), posterObject.type.dpi);

    return `${scale === 1 ? `${window.i18next.t('pmwjs_original')} · ` : ''}${Math.round(convertedWidth * scale)} x ${Math.round(convertedHeight * scale)}${PosterTypeUnits.PX}`;
  };

  const getValueForDisplay = (): string => {
    if (getUnitsFromPosterTypeObject() === PosterTypeUnits.PX) {
      const roundedWidth = Math.round(getWidthAndHeight().width * scale);
      const roundedHeight = Math.round(getWidthAndHeight().height * scale);

      return `${scale === 1 ? `${window.i18next.t('pmwjs_original')} · ` : ''}${roundedWidth} x ${roundedHeight}${getUnitsForDisplay()}`;
    }
    return `${scale === 1 ? `${window.i18next.t('pmwjs_original')} · ` : ''}${parseFloat((getWidthAndHeight().width * scale).toFixed(2))} x ${parseFloat((getWidthAndHeight().height * scale).toFixed(2))}${getUnitsForDisplay()}`;
  };

  const getOptionForImage = (): ReactElement => {
    return (
      <>
        <div className="flex flex-h-row justify-content-between spacing-m-b-2">
          <Text bold val={window.i18next.t('pmwjs_size')} size={TextSize.XSMALL} />
          <Text val={getValueForDisplay()} size={TextSize.XSMALL} />
        </div>
        <ControlledSlider marks={[1]} step={0.1} min={min} max={max} value={currentScale} onSlide={onSlide} className={styles.slider} />
      </>
    );
  };

  const getTextForVideoScale = (): string => {
    switch (videoScale) {
      case VideoDesignSizeOptionType.SD:
        return videoOriginalMatchingValue === VideoDesignSizeOptionType.SD ? `${window.i18next.t('pmwjs_original')} · 480p` : '480p';
      case VideoDesignSizeOptionType.HD:
        return videoOriginalMatchingValue === VideoDesignSizeOptionType.HD ? `${window.i18next.t('pmwjs_original')} · 720p` : '720p';
      case VideoDesignSizeOptionType.FULL_HD:
        return videoOriginalMatchingValue === VideoDesignSizeOptionType.FULL_HD ? `${window.i18next.t('pmwjs_original')} · 1080p · HD` : '1080p · HD';
      case VideoDesignSizeOptionType.UHD_4K:
        return videoOriginalMatchingValue === VideoDesignSizeOptionType.SD ? `${window.i18next.t('pmwjs_original')} · 2160p · 4K` : '2160p · 4K';
      case VideoDesignSizeOptionType.ORIGINAL:
        return `${isCustomSize() ? posterObject.userWidth : posterObject.type.width} x ${isCustomSize() ? posterObject.userHeight : posterObject.type.height}px · Original`;
      default:
        return '720p';
    }
  };

  const getOptionForMP4Video = (): ReactElement => {
    return (
      <>
        <div className="flex flex-h-row justify-content-between">
          <Text bold val={window.i18next.t('pmwjs_size')} size={TextSize.XSMALL} />
          <Text val={getTextForVideoScale()} size={TextSize.XSMALL} />
        </div>
        <ControlledSlider
          key={`${doShowOriginalValueForVideo}`}
          marks={doShowOriginalValueForVideo ? [1, 2, 3] : [1, 2]}
          step={1}
          min={0}
          max={doShowOriginalValueForVideo ? 4 : 3}
          value={videoSliderScale}
          onSlide={onSlideVideoScale}
          className={styles.slider}
        />
      </>
    );
  };

  const getOptionForGIF = (): ReactElement => {
    return (
      <>
        <div className="flex flex-h-row justify-content-between">
          <Text bold val={window.i18next.t('pmwjs_size')} size={TextSize.XSMALL} />
          <Text key={scale} val={getValueForDisplayForGif()} size={TextSize.XSMALL} />
        </div>
        <ControlledSlider key={`${min}-${max}`} marks={[1]} step={0.01} min={min} max={max} value={currentScale} onSlide={onSlideGifScale} className={styles.slider} />
      </>
    );
  };

  const getOptions = (): ReactElement => {
    if (fileType === FileTypeOptions.MP4) {
      return getOptionForMP4Video();
    }

    if (fileType === FileTypeOptions.GIF) {
      return getOptionForGIF();
    }

    return getOptionForImage();
  };

  return <div className={`spacing-p-t-2`}>{getOptions()}</div>;
}
